/**
 * Planning Tasks API Client (Epic C-1 — Fundament, Story C-1.3)
 *
 * AD-9: `lib/<resource>Api.ts`-conventie, zelfde patroon als
 * `coreTasksApi.ts`/`aanwezigheidApi.ts`. Consumeert
 * `/api/v1/planning/tasks*` (Story C-1.2).
 *
 * Story C-3.3 breidt dit bestand uit met het board-read-model
 * (`getBoard`, Story C-3.1) en de indeling-mutaties (`upsertAssignment`/
 * `deleteAssignment`, Story C-3.2) — zelfde `lib/<resource>Api.ts`-conventie,
 * geen apart bestand voor hetzelfde `/api/v1/planning/*`-domein.
 */

import { apiRequest } from './api';
import type {
  PlanningTaskDTO,
  CreatePlanningTaskInput,
  UpdatePlanningTaskInput,
  PlanningBoardResponse,
  PlanningOccupancyResponse,
  PlanningOccupancyGranularity,
  PlanningConflictsResponse,
  UpsertAssignmentItem,
  UpsertAssignmentResultDTO,
  PlanningDayPart,
  MyAssignmentsResponse,
} from '@functieloonhuis/shared';

interface DataResponse<T> {
  data: T;
}

export const planningApi = {
  listTasks: async (includeArchived = false): Promise<PlanningTaskDTO[]> => {
    const query = includeArchived ? '?includeArchived=true' : '';
    const res = await apiRequest<DataResponse<PlanningTaskDTO[]>>(`/planning/tasks${query}`);
    return res.data;
  },

  createTask: async (data: CreatePlanningTaskInput): Promise<PlanningTaskDTO> => {
    const res = await apiRequest<DataResponse<PlanningTaskDTO>>('/planning/tasks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  updateTask: async (id: number, data: UpdatePlanningTaskInput): Promise<PlanningTaskDTO> => {
    const res = await apiRequest<DataResponse<PlanningTaskDTO>>(`/planning/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return res.data;
  },

  archiveTask: async (id: number): Promise<PlanningTaskDTO> => {
    const res = await apiRequest<DataResponse<PlanningTaskDTO>>(`/planning/tasks/${id}/archive`, {
      method: 'POST',
    });
    return res.data;
  },

  restoreTask: async (id: number): Promise<PlanningTaskDTO> => {
    const res = await apiRequest<DataResponse<PlanningTaskDTO>>(`/planning/tasks/${id}/restore`, {
      method: 'POST',
    });
    return res.data;
  },

  /** Story C-3.1 — `GET /api/v1/planning/board?van&tot&departmentId`. */
  getBoard: async (van: string, tot: string, departmentId?: number): Promise<PlanningBoardResponse> => {
    const params = new URLSearchParams({ van, tot });
    if (departmentId !== undefined) params.set('departmentId', String(departmentId));
    const res = await apiRequest<DataResponse<PlanningBoardResponse>>(`/planning/board?${params.toString()}`);
    return res.data;
  },

  /** Story C-3.2 — `PUT /api/v1/planning/assignments` (1 item of "hele dag" = 2). */
  upsertAssignment: async (items: UpsertAssignmentItem[]): Promise<UpsertAssignmentResultDTO[]> => {
    const res = await apiRequest<DataResponse<UpsertAssignmentResultDTO[]>>('/planning/assignments', {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
    return res.data;
  },

  /** Story C-3.2 — `DELETE /api/v1/planning/assignments?employeeId&date&dayPart`. */
  deleteAssignment: async (employeeId: number, date: string, dayPart: PlanningDayPart): Promise<void> => {
    const params = new URLSearchParams({ employeeId: String(employeeId), date, dayPart });
    await apiRequest<void>(`/planning/assignments?${params.toString()}`, { method: 'DELETE' });
  },

  /** Story C-4.1 — `GET /api/v1/planning/occupancy?van&tot&departmentId&granularity`. */
  getOccupancy: async (
    van: string,
    tot: string,
    departmentId?: number,
    granularity: PlanningOccupancyGranularity = 'week'
  ): Promise<PlanningOccupancyResponse> => {
    const params = new URLSearchParams({ van, tot, granularity });
    if (departmentId !== undefined) params.set('departmentId', String(departmentId));
    const res = await apiRequest<DataResponse<PlanningOccupancyResponse>>(`/planning/occupancy?${params.toString()}`);
    return res.data;
  },

  /** Story C-4.2 — `GET /api/v1/planning/conflicts?van&tot&departmentId`. */
  getConflicts: async (van: string, tot: string, departmentId?: number): Promise<PlanningConflictsResponse> => {
    const params = new URLSearchParams({ van, tot });
    if (departmentId !== undefined) params.set('departmentId', String(departmentId));
    const res = await apiRequest<DataResponse<PlanningConflictsResponse>>(`/planning/conflicts?${params.toString()}`);
    return res.data;
  },

  /** Story C-5.1 — `GET /api/v1/planning/assignments/me?van&tot` (strikt eigen-scope, max 14 dagen). */
  getMyAssignments: async (van: string, tot: string): Promise<MyAssignmentsResponse> => {
    const params = new URLSearchParams({ van, tot });
    const res = await apiRequest<DataResponse<MyAssignmentsResponse>>(`/planning/assignments/me?${params.toString()}`);
    return res.data;
  },
};
